import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { useAppSelector, useAppDispatch } from '../../redux/store';
import { useAuth } from '../../contexts/AuthContext';
import { setDiscoveredOrganizations, setIntermediateSessionToken } from '../../redux/slices/organizationSlice';
import organizationService from '../../services/organization.service';
import OrganizationCard from './OrganizationCard';
import OrganizationList from './OrganizationList';

interface OrganizationSwitcherProps {
    currentUser: any;
}

/**
 * OrganizationSwitcher - Functional Container Component
 * Manages organization switching with lazy-loaded discovery
 * Fetches organizations from Stytch when component mounts
 */
const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({ currentUser }) => {
    const { t } = useTranslation();
    const { switchOrganization } = useAuth();
    const dispatch = useAppDispatch();

    // Read from Redux
    const currentOrganization = useAppSelector(state => state.organization.currentOrganization);
    const discoveredOrganizations = useAppSelector(state => state.organization.discoveredOrganizations);
    const intermediateSessionToken = useAppSelector(state => state.organization.intermediateSessionToken);

    // Local state
    const [isLoading, setIsLoading] = useState(false);
    const [isSwitching, setIsSwitching] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch organizations on mount
    useEffect(() => {
        const fetchOrganizations = async () => {
            // Only fetch if we don't have organizations yet
            if (discoveredOrganizations.length > 0) {
                return;
            }

            try {
                setIsLoading(true);
                setError(null);

                const data = await organizationService.discoverOrganizations();

                dispatch(setDiscoveredOrganizations(data.discoveredOrganizations));
                if (data.intermediateSessionToken) {
                    dispatch(setIntermediateSessionToken(data.intermediateSessionToken));
                }
            } catch (err: any) {
                console.error('Failed to discover organizations:', err);
                setError(err.response?.data?.error || err.message || t('organization.switcher.switchFailed'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchOrganizations();
    }, []); // Only run on mount

    const handleSwitch = async (organizationId: string) => {
        // If we have an intermediate token, use it (Login Flow)
        // If not, we rely on the session token (Settings Flow)
        // The AuthContext will handle finding the session token if IST is null
        try {
            setIsSwitching(true);
            setError(null);

            await switchOrganization(organizationId, intermediateSessionToken || '');

            // Success - AuthContext will handle navigation and state updates
        } catch (err: any) {
            console.error('Failed to switch organization:', err);
            setError(err.response?.data?.error || err.message || t('organization.switcher.switchFailed'));
        } finally {
            setIsSwitching(false);
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <ArrowLeftRight className="h-5 w-5" />
                    {t('organization.switcher.title')}
                </CardTitle>
                <CardDescription>
                    {t('organization.switcher.description')}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Error Alert */}
                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* Switching Indicator */}
                {isSwitching && (
                    <Alert>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <AlertDescription>
                            {t('organization.switcher.switching')}
                        </AlertDescription>
                    </Alert>
                )}

                {/* Current Organization */}
                {currentOrganization && (
                    <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-3">
                            {t('organization.switcher.currentOrganization')}
                        </h3>
                        <OrganizationCard
                            organization={{
                                organization_id: currentOrganization.stytchOrganizationId,
                                organization_name: currentOrganization.name,
                                member_id: currentUser?.stytchMemberId || '',
                                membership: {
                                    type: currentUser?.organizationRole || 'member',
                                    details: {}
                                }
                            }}
                            isCurrentOrganization={true}
                            onSwitch={() => { }}
                            disabled={true}
                        />
                    </div>
                )}

                {/* Other Organizations */}
                <OrganizationList
                    organizations={discoveredOrganizations}
                    currentOrganizationId={currentOrganization?.stytchOrganizationId || null}
                    onSwitch={handleSwitch}
                    disabled={isSwitching}
                />
            </CardContent>
        </Card>
    );
};

export default OrganizationSwitcher;
